
import UserImgInfo from '@/components/ui/molecule/UserImgInfo';
import SocialInteraction from '@/components/ui/molecule/SocialInteraction';
import MultipleImageDisplay from '@/components/ui/molecule/MultipleImageDisplay';
import { generateUID } from '@/utils';
import UIPost from '@/components/ui/organism/UIPost';
import { getMedia, isMobileResponsive } from '@/utils/mixins';
import PostMore from '@/components/ui/molecule/PostMore';
import TruncateParagraph from '@/components/ui/atom/TruncateParagraph';
import Embed from '@/components/ui/molecule/Embed';
import PostLeagueCard from '@/components/ui/molecule/PostLeagueCard';
import PollVote from '@/components/ui/molecule/PollVote';
import { inAppRedirect } from '~/utils/route-redirects';
import { appURLs } from '@/utils/route-redirects';
import { getBaseRouteName } from '@/utils/routeNames';
import { mapActions, mapGetters } from 'vuex';
import { FILTER_INTERVAL_KEYS } from '~/utils/filter-intervals';
import { eventManager } from '@/utils/behavior/applicationEvents/event-manager';
import { elementImpressionObserver } from '~/mixins/elementImpressionObserver';
import { formatPostContent } from '~/utils/links';
import { applyCustomFormat } from '@/utils/custom-format.ts';
import UITransactionPost from '../molecule/UITransactionPost.vue';
import UIPostGuideCard from '../molecule/UIPostGuideCard.vue';
import { ImageFormat } from '~/utils/behavior/imageUrl';
import UIPostTickerList from './UIPostTickerList.vue';
import PortfolioCardWithDonutChart from '../molecule/PortfolioCardWithDonutChart.vue';
import PortfolioCardWithDonutChartSkeleton from '@/components/ui/atom/skeleton/PortfolioCardWithDonutChartSkeleton.vue';
import { LeagueTabs } from '~/utils/constants/leagueConstants';

export default {
  name: 'UIProfilePost',
  components: {
    Embed,
    UserImgInfo,
    PostMore,
    UIPost,
    PollVote,
    SocialInteraction,
    MultipleImageDisplay,
    TruncateParagraph,
    PortfolioCardWithDonutChart,
    UITransactionPost,
    CreatePostWrapper: () => import('@/components/socials/CreatePostWrapper'),
    UIPostGuideCard,
    UIPostTickerList,
    PortfolioCardWithDonutChartSkeleton,
    PostLeagueCard,
  },
  mixins: [getMedia, isMobileResponsive, elementImpressionObserver],
  props: {
    disableTruncate: {
      type: Boolean,
      default: false,
    },
    post: {
      type: Object,
      default: () => null,
    },
    id: {
      type: String,
      default: '',
    },
    isReply: {
      type: Boolean,
      default: false,
    },
    disableHover: {
      type: Boolean,
      default: false,
    },
    body: {
      type: String,
      default: '',
    },
    media: {
      type: Array,
      default: () => [],
    },
    showSocialInteraction: {
      type: Boolean,
      default: true,
    },
    isSocialInteractionDisabled: {
      type: Boolean,
      default: false,
    },
    showOptions: {
      type: Boolean,
      default: true,
    },
    showCard: {
      type: Boolean,
      default: true,
    },
    isMobileReply: {
      type: Boolean,
      default: false,
    },
    isRepost: {
      type: Boolean,
      default: false,
    },
    alignmentDirection: {
      type: String,
      default: 'center',
    },
    socialInteractionsGap: {
      type: String,
      default: '4',
    },
    league: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      htmlBody: '',
      mentionIds: [],
      mentionedPost: null,
      appURLs,
      portfolio: null,
      isEdit: false,
      editText: '',
      imageFormats: ImageFormat,
    };
  },
  computed: {
    ...mapGetters('portfolio', ['getPortfolioByIdRes']),
    ...mapGetters('authentication', ['profileId']),
    ...mapGetters({
      updatedPost: 'post/post',
    }),
    fromNow() {
      if (this.post.isEdited && this.post.editedDate) {
        const updatedFromNow = this.$dayjs(this.post.editedDate)
          .subtract('1', 's')
          .startOf('s')
          .fromNow();
        return `(${this.$t('post_edited')} ${updatedFromNow})`;
      } else {
        return this.$dayjs(this.post.createdDate)
          .subtract('1', 's')
          .startOf('s')
          .fromNow();
      }
    },
    // TODO refactor all instancesd of this.$route.name in computed to handle the name being null in case the user types a route that doesn't exist
    isMobile() {
      return window.screen.width <= 640;
    },
    idSuffix() {
      return this.isRepost
        ? `${this.post.postId}-${generateUID()}`
        : this.post.postId;
    },
    portfolioId() {
      return this.post && this.post.transaction
        ? this.post.transaction.portfolioId
        : null;
    },
    showPostLeagueCard() {
      const routeName = getBaseRouteName(this.$route);
      return this.post.leagueInfo && routeName === appURLs.postById().name;
    },
    // League prop is used when coming from a league feed as leagueInfo is not available there, leagueInfo is available on post details page
    effectiveLeague() {
      return this.league || this.post.leagueInfo;
    },
  },
  watch: {
    body() {
      this.buildBody();
    },
    isEdit(value) {
      if (value) this.editText = this.htmlBody;
    },
  },
  beforeCreate: function () {
    this.$options.components.UIPost =
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require('@/components/ui/organism/UIPost').default;
  },
  created() {
    this.buildBody();
    this.getPostPortfolio();
  },

  mounted() {
    this.setupIntersectionObserver({
      targetRef: 'postElement',
      onImpression: () => this.raisePostImpressionEvent(),
    });
    this.bindBodyEvent();
  },
  beforeDestroy() {
    this.removeBodyEvent();
  },
  methods: {
    ...mapActions('portfolio', [
      'getPortfolioById',
      'likePortfolio',
      'favoritePortfolioWithoutUpdate',
    ]),
    ...mapActions('post', ['getPost', 'getReplies', 'getNestedReplies']),
    ...mapActions('modals', ['showCreatePostModal']),
    ...mapActions({
      updatePostOnHomeFeed: 'home/updatePostAfterEdit',
      updatePostOnProfileFeed: 'profile/updatePostAfterEdit',
      updatePostOnPostPage: 'post/updatePostAfterEdit',
      updatePostOnLeagueFeed: 'leagues/updatePostAfterEdit',
    }),
    buildBody() {
      //TODO : TBD : This is handled in the API when creating the news post, still there for old news posts
      this.htmlBody = this.body;

      this.removeBodyEvent();

      if (this.post.customFormat) {
        this.htmlBody = applyCustomFormat(
          this.post.body,
          this.post.customFormat
        );
      }
      if (this.post.mentions && this.post.mentions.length) {
        this.post.mentions.forEach((mention) => {
          if (mention.type === '@') {
            const id = generateUID();
            const profilePath = `/${appURLs.profile()}/${
              mention.profile.profileId
            }`;
            this.htmlBody = this.htmlBody.replaceAll(
              `${mention.type}${mention.target}`,
              `<span class="${id} mention cursor-pointer" data-id="${mention.profile.profileId}"><a data-id="${mention.profile.profileId}" href="${profilePath}">${mention.type}${mention.profile.displayName}</a></span>`
            );
            this.mentionIds.push(id);
          } else if (mention.type === '#') {
            this.htmlBody = this.htmlBody.replaceAll(
              `${mention.type}${mention.target}`,
              `<a href="/search-hashtags?hashtag=${mention.target}" class="mention cursor-pointer">${mention.type}${mention.target}</a>`
            );
          } else if (mention.type === 'POST') {
            this.mentionedPost = mention.post;
          }
        });
      }
      this.htmlBody = formatPostContent(this.htmlBody);
    },
    handleMouseEnterBodyMention(e) {
      if (e && e.target) {
        const profileMentioned = this.post.mentions.find(
          (elem) => elem.target === e.target.dataset.id
        );
        this.$emit('mouseEnterMention', {
          pos: {
            top: e.target.offsetTop + (e.target.offsetHeight + 8),
            left: e.target.offsetLeft,
          },
          profile: profileMentioned.profile,
          id: e.target.dataset.id,
        });
      }
    },
    handleMouseOutBodyMention(e) {
      if (e && e.target) {
        this.$emit('mouseOutMention', e.target.dataset.id);
      }
    },
    bindBodyEvent() {
      if (this.isMobile) {
        // We don't want mention mention event attached on mobile
        return;
      }
      this.mentionIds.forEach((id) => {
        const mentionNodes = document.getElementsByClassName(id);
        for (const node of mentionNodes) {
          node.addEventListener('mouseenter', this.handleMouseEnterBodyMention);
          node.addEventListener('mouseout', this.handleMouseOutBodyMention);
          node.addEventListener('click', this.handleMouseOutBodyMention, true);
        }
      });
    },
    removeBodyEvent() {
      this.mentionIds.forEach((id) => {
        if (!this.$refs.body) return;
        const mentionNodes = this.$refs.body.getElementsByClassName(id);

        for (const node of mentionNodes) {
          node.removeEventListener(
            'mouseenter',
            this.handleMouseEnterBodyMention
          );
          node.removeEventListener('mouseout', this.handleMouseOutBodyMention);
          node.removeEventListener(
            'click',
            this.handleMouseOutBodyMention,
            true
          );
        }
      });
      this.mentionIds = [];
    },
    goToProfile(profileId, externalUri = null) {
      if (externalUri) {
        window.open(externalUri);
      } else {
        inAppRedirect(`/profile/${profileId}`);
      }
    },
    goToPost(postId) {
      inAppRedirect(`/post/${postId}`);
    },
    goToLeague(leagueId) {
      inAppRedirect(appURLs.leagueById(leagueId, LeagueTabs.DISCUSSION));
    },
    handlePortfolioTagClick(portfolioId) {
      inAppRedirect(
        this.localePath({
          path: `/portfolios/${portfolioId}`,
          query: { topic: 'conversation' },
        })
      );
    },
    handleCompanyTagClick(profileId) {
      inAppRedirect(
        this.localePath({
          path: `/profile/${profileId}`,
          query: { topic: 'conversation' },
        })
      );
    },
    getProfilePath(profileId) {
      return this.localePath(`/${appURLs.profile()}/${profileId}`);
    },
    async getPostPortfolio() {
      if (this.post?.portfolioId) {
        await this.getPortfolioById(this.post.portfolioId);
        this.portfolio = this.getPortfolioByIdRes.data;
      }
    },
    handlePortfolioLike(id) {
      this.likePortfolio(id);
    },
    handleProfileClick(id) {
      this.$emit('onClose');
      inAppRedirect(`/profile/${id}`);
    },
    handlePortfolioClick(id) {
      this.$emit('onClose');
      inAppRedirect(
        this.localePath(
          appURLs.portfolioByGraphInterval(FILTER_INTERVAL_KEYS.YTD, id)
        )
      );
    },
    handleFavoritePortfolio(id) {
      this.favoritePortfolioWithoutUpdate(id).then((res) => {
        this.portfolio.socials.isFavorited = res.data.isFavorited;
        this.portfolio.socials.favoriteCount = res.data.favoriteCount;
      });
    },
    // Allow handlers only on the post page
    handleCardClick(event) {
      switch (event) {
        case 'like':
          this.handlePortfolioLike(this.portfolio.id);
          break;
        case 'favorite':
          this.handleFavoritePortfolio(this.portfolio.id);
          break;
        case 'portfolio':
          this.handlePortfolioClick(this.portfolio.id);
          break;
        case 'profile':
          this.handleProfileClick(this.portfolio.owner.ownerId);
          break;
        case 'comment':
          this.handlePortfolioTagClick(this.portfolio.id);
          break;
      }
    },
    async handlePostUpdate(data) {
      await this.getPost(data.postId);
      await this.getReplies({
        postId: data.postId,
        params: { limit: 10, orderBy: 'CreatedDateDescending' },
      });
      await this.getNestedReplies({
        postId: data.postId,
        params: { limit: 10, orderBy: 'CreatedDateDescending' },
      });

      if (this.updatedPost) {
        const isProfileRoute = this.$route.fullPath.includes(appURLs.profile());
        const isHomeRoute = this.$route.name?.includes('index');
        const isPostRoute = this.$route.name?.includes('post');
        const isLeagueRoute = this.$route.name?.includes('leagues-leagueId');

        if (isLeagueRoute) {
          this.updatePostOnLeagueFeed(this.updatedPost);
        } else if (isProfileRoute) {
          this.updatePostOnProfileFeed(this.updatedPost);
        } else if (isHomeRoute) {
          this.updatePostOnHomeFeed(this.updatedPost);
        } else if (isPostRoute) this.updatePostOnPostPage(this.updatedPost);
      }

      this.resetEdit();
    },
    resetEdit() {
      this.isEdit = false;
      this.editText = '';
    },
    raisePostImpressionEvent() {
      if (this.post) {
        eventManager.raisePostImpressionEvent({
          post_id: this.post.postId,
          post_author: this.post.author?.profileName || '',
          post_author_id: this.post.author?.profileId || '',
        });
      }
    },
    handleTransactionCardClick() {
      const companyId = this.post.transaction?.companyId;
      if (companyId) {
        inAppRedirect(appURLs.profileById(companyId));
      }
    },
    handleGuideCardClick(guideId) {
      inAppRedirect(appURLs.academyLesson(guideId));
    },
  },
};
